import React from "react";

interface EditableProps {
  text: string;
  onEdit?: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit = () => {},
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  return (
    <div
      contentEditable
      onBlur={(e) => onEdit(e.currentTarget.innerText)}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {text || placeholder}
    </div>
  );
};

const IntroStatementSlide = {
  id: "intro-statement-slide",
  name: "Introductory Statement Slide",
  fields: [
    {
      id: "headline",
      label: "Headline or Question",
      type: "text",
      defaultValue: "Why Study Photosynthesis?",
    },
    {
      id: "statement",
      label: "Introductory Statement",
      type: "text",
      defaultValue:
        "It is the foundation of most food chains and the source of our planet's oxygen.",
    },
    {
      id: "highlights",
      label: "Key Highlights (comma separated)",
      type: "text",
      defaultValue:
        "Essential for Life, Powers Ecosystems, Sustains Agriculture",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #065f46 0%, #047857 50%, #059669 100%)",
    },
  ],
  render: (content: any) => {
    const {
      headline,
      statement,
      highlights,
      bgColor,
      onEdit = () => {},
    } = {
      headline: content.headline || "Why Study Photosynthesis?",
      statement:
        content.statement ||
        "It is the foundation of most food chains and the source of our planet's oxygen.",
      highlights:
        content.highlights ||
        "Essential for Life, Powers Ecosystems, Sustains Agriculture",
      bgColor:
        content.bgColor ||
        "linear-gradient(135deg, #065f46 0%, #047857 50%, #059669 100%)",
      onEdit: content.onEdit || (() => {}),
    };

    const handleEdit = (field: string) => (value: string) => {
      onEdit({ ...content, [field]: value });
    };

    const handleHighlightEdit = (index: number) => (value: string) => {
      const highlightArray = Array.isArray(highlights)
        ? [...highlights]
        : highlights.split(",").map((h: string) => h.trim());
      highlightArray[index] = value;
      onEdit({
        ...content,
        highlights: Array.isArray(highlights)
          ? highlightArray
          : highlightArray.join(", "),
      });
    };

    const highlightArray = Array.isArray(highlights)
      ? highlights
      : highlights.split(",").map((h: string) => h.trim());

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-32 bg-white/5 transform -skew-y-6" />
          <div className="absolute bottom-0 right-0 w-full h-32 bg-white/5 transform skew-y-6" />
        </div>

        {/* Floating Circles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-10 w-64 h-64 bg-emerald-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-emerald-600/10 rounded-full blur-3xl" />
        </div>

        {/* Side Decoration */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-32">
          <div className="w-full h-full bg-gradient-to-b from-transparent via-emerald-300/50 to-transparent" />
        </div>

        {/* Main Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-16 py-12">
          <div className="max-w-5xl mx-auto text-center">
            {/* Question Mark Icon */}
            <div className="mb-8 relative">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
                <span className="text-4xl font-bold text-emerald-100">?</span>
              </div>
              <div className="absolute -inset-2 bg-emerald-500/10 rounded-full blur-md -z-10" />
            </div>

            {/* Headline */}
            <h1 className="text-6xl font-black mb-8 tracking-tight relative">
              <EditableDiv
                text={headline}
                onEdit={handleEdit("headline")}
                className="relative text-emerald-100"
              />
            </h1>

            {/* Main Statement */}
            <EditableDiv
              text={statement}
              onEdit={handleEdit("statement")}
              className="text-3xl font-light mb-12 text-emerald-50 leading-relaxed max-w-4xl mx-auto"
            />

            {/* Highlights */}
            <div className="grid grid-cols-3 gap-6 mt-8">
              {highlightArray.map((highlight: string, index: number) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-lg p-4 transform hover:scale-105 transition-transform"
                >
                  <div className="w-2 h-2 bg-emerald-400/50 rounded-full mx-auto mb-3" />
                  <EditableDiv
                    text={highlight}
                    onEdit={handleHighlightEdit(index)}
                    className="text-lg font-medium text-emerald-100"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Decoration */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
          <div className="w-12 h-1 rounded-full bg-gradient-to-r from-emerald-300/60 to-emerald-300/20" />
          <div className="w-6 h-1 rounded-full bg-gradient-to-r from-emerald-300/40 to-emerald-300/10" />
          <div className="w-4 h-1 rounded-full bg-gradient-to-r from-emerald-300/20 to-emerald-300/5" />
        </div>
      </div>
    );
  },
};

export default IntroStatementSlide;
